/** Kachel „Geräte" auf der Startseite: Spülmaschine, Waschmaschine, Trockner. */
import { actionQueue, cachedGet, currentMe, enqueueAction } from '../store.js';
import { overlayRuns } from '../optimistic.js';
import { esc, toast } from '../ui.js';
import {
  expectedEnd, finishedAt, formatRemaining, isActive, runPhase, type ApplianceDTO, type RunDTO,
} from '../../shared/appliances.js';
import { berlinDate, berlinTime, formatHM } from '../../shared/time.js';

interface AppliancesResponse {
  appliances: ApplianceDTO[];
  runs: RunDTO[];
  serverTime: number;
}

export interface AppliancesData {
  appliances: ApplianceDTO[];
  runs: RunDTO[];
  /** Zeitpunkt des Serverstands */
  stand: number | null;
  offline: boolean;
}

export async function loadAppliances(): Promise<AppliancesData> {
  let data: AppliancesResponse | null = null;
  let offline = true;
  try {
    const r = await cachedGet<AppliancesResponse>('appliances', '/appliances');
    data = r.data;
    offline = r.offline;
  } catch {
    /* Cache leer */
  }
  const me = currentMe();
  const runs = me ? overlayRuns(data?.runs ?? [], await actionQueue.list(), me.user) : data?.runs ?? [];
  return { appliances: data?.appliances ?? [], runs, stand: data?.serverTime ?? null, offline };
}

function timeLabel(ms: number, now: number): string {
  return berlinDate(ms) === berlinDate(now) ? berlinTime(ms) : `${new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} ${berlinTime(ms)}`;
}

function card(a: ApplianceDTO, run: RunDTO | undefined, now: number): string {
  const phase = run ? runPhase(run, now) : null;
  const watts = a.hasSensor && a.lastWatts !== null && a.lastPowerAt && now - a.lastPowerAt < 10 * 60_000
    ? `<span class="watts" title="aktuelle Leistung laut Steckdose">⚡ ${Math.round(a.lastWatts).toLocaleString('de-DE')} W</span>` : '';
  const who = run ? (run.startedByName ? `gestartet von ${esc(run.startedByName)}` : 'von der Steckdose erkannt') : '';
  const pending = run?.pending ? ' <small>(wird hochgeladen)</small>' : '';
  let state = '<span class="appl-state">frei</span>';
  let detail = '';
  let actions = `<button type="button" class="btn small" data-open="${a.id}">Starten</button>`;
  if (run && phase === 'laeuft') {
    const end = expectedEnd(run);
    state = `<span class="appl-state">läuft · noch <span data-until="${end}">${formatRemaining(end - now)}</span></span>`;
    detail = `${a.name} läuft – ${who} ${esc(timeLabel(run.startedAt, now))}, fertig ca. ${esc(timeLabel(end, now))}${run.sensorConfirmedAt ? ' · Steckdose ✓' : ''}${pending}`;
    actions = `
      <button type="button" class="btn small" data-extend="${esc(run.id)}" data-min="15">+15 min</button>
      <button type="button" class="btn small" data-extend="${esc(run.id)}" data-min="30">+30 min</button>
      <button type="button" class="btn small" data-done="${esc(run.id)}">Schon fertig &amp; ausgeräumt</button>
      <button type="button" class="btn small danger" data-cancel="${esc(run.id)}">Abbrechen</button>`;
  } else if (run && phase === 'fertig') {
    state = '<span class="appl-state">fertig – bitte ausräumen</span>';
    detail = `Fertig seit ${esc(timeLabel(finishedAt(run), now))}${run.finishedSource === 'sensor' ? ' (laut Steckdose)' : ''} · ${who} ${esc(timeLabel(run.startedAt, now))}${pending}`;
    actions = `<button type="button" class="btn small primary" data-done="${esc(run.id)}">Ausgeräumt / erledigt</button>
      <button type="button" class="btn small" data-extend="${esc(run.id)}" data-min="15">Läuft noch: +15 min</button>`;
  }
  const programs = a.programs.length ? a.programs : [{ label: 'Standard', minutes: a.defaultMinutes }];
  return `<div class="appl phase-${phase ?? 'frei'}">
    <div class="appl-head"><span class="appl-icon" aria-hidden="true">${esc(a.icon)}</span><strong>${esc(a.name)}</strong>${state}</div>
    ${detail || watts ? `<p class="small appl-detail">${detail} ${watts}</p>` : ''}
    <div class="appl-actions">${actions}</div>
    ${!run || (phase !== 'laeuft' && phase !== 'fertig') ? `
    <form class="appl-start" data-appliance="${a.id}" hidden>
      <div class="programs">${programs
        .map((p) => `<button type="button" class="btn small" data-start="${a.id}" data-min="${p.minutes}">${esc(p.label)} ${formatHM(p.minutes)}</button>`)
        .join('')}</div>
      <div class="row"><input name="minutes" type="number" min="1" max="600" inputmode="numeric" placeholder="Minuten" aria-label="Dauer in Minuten">
        <button class="btn small primary">Starten</button></div>
    </form>` : ''}
  </div>`;
}

export function appliancesHtml(d: AppliancesData, now = Date.now()): string {
  if (!d.appliances.length) return '';
  const active = new Map(d.runs.filter(isActive).map((r) => [r.applianceId, r]));
  const stand = d.offline && d.stand ? `<p class="muted small">Offline – Stand von ${esc(timeLabel(d.stand, now))}</p>` : '';
  return `<section class="appliances"><div class="section-head"><h2>Geräte</h2></div>${stand}
    ${d.appliances.map((a) => card(a, active.get(a.id), now)).join('')}</section>`;
}

/** Knöpfe verdrahten; `rerender` zeichnet die Kachel neu. */
export function bindAppliances(root: HTMLElement, rerender: () => void): void {
  const run = async (type: string, payload: Record<string, unknown>, msg: string) => {
    await enqueueAction(type, payload);
    toast(navigator.onLine ? msg : `${msg} – wird hochgeladen, sobald Netz da ist.`);
    rerender();
  };
  root.querySelectorAll<HTMLButtonElement>('[data-open]').forEach((b) =>
    b.addEventListener('click', () => {
      const f = root.querySelector<HTMLFormElement>(`form[data-appliance="${b.dataset.open}"]`);
      if (f) f.hidden = !f.hidden;
    }),
  );
  root.querySelectorAll<HTMLButtonElement>('[data-start]').forEach((b) =>
    b.addEventListener('click', () => void run('appliance.start', { applianceId: Number(b.dataset.start), minutes: Number(b.dataset.min) }, 'Gestartet')),
  );
  root.querySelectorAll<HTMLFormElement>('form.appl-start').forEach((f) =>
    f.addEventListener('submit', (ev) => {
      ev.preventDefault();
      const minutes = Number((f.elements.namedItem('minutes') as HTMLInputElement).value);
      if (!(minutes >= 1 && minutes <= 600)) {
        toast('Bitte eine Dauer zwischen 1 und 600 Minuten eingeben.', 'error');
        return;
      }
      void run('appliance.start', { applianceId: Number(f.dataset.appliance), minutes }, 'Gestartet');
    }),
  );
  root.querySelectorAll<HTMLButtonElement>('[data-extend]').forEach((b) =>
    b.addEventListener('click', () => void run('appliance.extend', { runId: b.dataset.extend, minutes: Number(b.dataset.min) }, `Um ${b.dataset.min} Minuten verlängert`)),
  );
  root.querySelectorAll<HTMLButtonElement>('[data-done]').forEach((b) =>
    b.addEventListener('click', () => void run('appliance.done', { runId: b.dataset.done }, 'Erledigt – danke!')),
  );
  root.querySelectorAll<HTMLButtonElement>('[data-cancel]').forEach((b) =>
    b.addEventListener('click', () => {
      const reason = prompt('Abbrechen – Grund (optional):', '');
      if (reason === null) return;
      void run('appliance.cancel', { runId: b.dataset.cancel, reason }, 'Abgebrochen');
    }),
  );
}
